import { OrderTagPill } from '@euler/app/components/order/OrderTagPill';
import { Icon } from '@euler/components/Icomoon';
import { useShowFloatingTextInput } from '@euler/components/input/FloatingTextInput';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useOrderTags } from '@euler/functions/useOrderTags';
import { useStoreConfiguration } from '@euler/functions/useStoreConfiguration';
import { isNotNull } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { memo, Suspense } from 'react';
import { ActivityIndicator, StyleProp, View, ViewStyle } from 'react-native';
import { useTheme } from 'styled-components';

type Props = {
  tags: string[] | undefined;
  style?: StyleProp<ViewStyle>;
  title?: string;
  description?: string;
  placeholder?: string;
  editable?: boolean;
  onTagRemoved?: (tag: string) => void;
  onAddTag?: (tag: string) => void;
};

export const kEnableCustomOrderTags = false;

export const OrderTagList = memo((props: Props) => {
  const { rpx } = useResponsiveContext();
  return (
    <Suspense
      fallback={
        <View
          nativeID="order-tags-suspense"
          css={`
            flex-direction: row;
            justify-content: flex-end;
            margin-right: ${rpx(-10)}px;
            align-self: flex-end;
          `}
        >
          <ActivityIndicator size={'small'} />
        </View>
      }
    >
      <OrderTagListContent {...props} />
    </Suspense>
  );
});

const OrderTagListContent = memo(
  ({
    title,
    description,
    placeholder,
    editable = true,
    tags,
    style,
    onAddTag,
    onTagRemoved,
  }: Props) => {
    const { rpx } = useResponsiveContext();
    const storeConf = useStoreConfiguration();
    const predefinedTags = useOrderTags();
    const { showActionSheetWithOptions } = useActionSheet();
    const showCustomTextControl = useShowFloatingTextInput();
    const { link } = useTheme();
    const onSelect = usePersistFn(() => {
      const predefinedTagList = (storeConf?.orderTags ?? predefinedTags).map(
        x => x.name,
      );
      const cancelButtonIndex =
        predefinedTagList.length + (kEnableCustomOrderTags ? 1 : 0);
      showActionSheetWithOptions(
        {
          title: title ?? '选择业务类型',
          message: description,
          options: [
            ...predefinedTagList,
            kEnableCustomOrderTags ? '自定义...' : null,
            '关闭',
          ].filter(isNotNull),
          cancelButtonIndex,
        },
        async buttonIndex => {
          if (buttonIndex == null || buttonIndex === cancelButtonIndex) {
            return;
          }
          if (buttonIndex < predefinedTagList.length) {
            onAddTag?.(predefinedTagList[buttonIndex]);
          } else if (
            kEnableCustomOrderTags &&
            buttonIndex === predefinedTagList.length
          ) {
            showCustomTextControl({
              maxNumberOfLines: 1,
              placeholder: '输入自定义标签名称',
              noToolbarPlaceholder: true,
              onDone: value => onAddTag?.(value),
            });
          }
        },
      );
    });
    return (
      <View
        css={`
          flex-direction: row;
          justify-content: flex-start;
          align-items: center;
          flex-wrap: wrap;
        `}
        style={style}
      >
        {tags?.map(tag => (
          <OrderTagPill
            tag={tag}
            key={tag}
            onRemove={editable ? onTagRemoved : undefined}
            css={`
              margin-right: ${rpx(5)}px;
            `}
          />
        ))}
        {editable && (
          <OrderTagPill
            tag={placeholder ?? '选择标签'}
            icon={
              <Icon
                name="plus-circle"
                color={link}
                size={rpx(18)}
                css={`
                  margin-right: ${rpx(5)}px;
                `}
              />
            }
            onPress={onSelect}
          />
        )}
      </View>
    );
  },
);
